import React, { useState, useCallback } from "react";

/**
 * Impersonation indicator that shows in the navigation bar.
 * Displays the impersonated user's info and an exit button.
 */
// Only treat the session as impersonation when every piece lines up:
// the flag is set, the admin session is saved for restore, and the ACTIVE
// session token actually belongs to the impersonated client. This prevents
// a stale banner from showing after the admin re-logs in on their own token.
const isActiveImpersonation = () => {
  try {
    if (localStorage.getItem("isImpersonating") !== "true") return false;
    if (!localStorage.getItem("adminClientData")) return false;
    const clientData = JSON.parse(localStorage.getItem("clientData") || "null");
    const impersonatedClient = JSON.parse(localStorage.getItem("impersonatedClient") || "null");
    if (!clientData || !impersonatedClient) return false;
    return String(clientData.clientId) === String(impersonatedClient.id);
  } catch {
    return false;
  }
};

const ImpersonationBanner = () => {
  const [isExiting, setIsExiting] = useState(false);
  const isImpersonating = isActiveImpersonation();

  const stopImpersonation = useCallback(async () => {
    if (isExiting) return;
    setIsExiting(true);

    try {
      // Get the impersonation token to delete it from database
      const clientData = localStorage.getItem("clientData");
      let impersonationToken = null;
      if (clientData) {
        try {
          const parsed = JSON.parse(clientData);
          impersonationToken = parsed.token;
        } catch (e) {
          // ignore
        }
      }

      // Get admin token for authorization
      const adminData = localStorage.getItem("adminClientData");
      let adminToken = null;
      if (adminData) {
        try {
          const parsed = JSON.parse(adminData);
          adminToken = parsed.token;
        } catch (e) {
          // ignore
        }
      }

      // Call backend to remove impersonation token from database
      if (impersonationToken && adminToken) {
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/stop-impersonation`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({ token: impersonationToken }),
          });
        } catch (e) {
          // Don't block exit if API fails
          console.error("Failed to remove impersonation token:", e);
        }
      }

      // Restore admin session
      if (adminData) {
        localStorage.setItem("clientData", adminData);
      }

      // Clear impersonation data
      localStorage.removeItem("adminClientData");
      localStorage.removeItem("isImpersonating");
      localStorage.removeItem("impersonatedClient");

      // Redirect to admin page
      window.location.href = "/admin";
    } catch (err) {
      console.error("Stop impersonation error:", err);
      setIsExiting(false);
    }
  }, [isExiting]);

  if (!isImpersonating) {
    return null;
  }

  let client = {};
  try {
    client = JSON.parse(localStorage.getItem("impersonatedClient") || "{}");
  } catch (e) {
    // ignore parse errors
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
        padding: "6px 14px",
        borderRadius: "6px",
        marginRight: "10px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
      }}
    >
      <span style={{ color: "white", fontSize: "13px", whiteSpace: "nowrap" }}>
        <strong style={{ marginRight: "4px" }}>⚠️</strong>
        <strong>{client.name || "Kullanıcı"}</strong>
        {client.company?.name && (
          <span style={{ opacity: 0.9, marginLeft: "4px" }}>({client.company.name})</span>
        )}
      </span>
      <button
        onClick={stopImpersonation}
        disabled={isExiting}
        style={{
          background: "white",
          color: "#f57c00",
          border: "none",
          padding: "4px 10px",
          borderRadius: "4px",
          cursor: isExiting ? "wait" : "pointer",
          fontWeight: "bold",
          fontSize: "12px",
          whiteSpace: "nowrap",
          opacity: isExiting ? 0.7 : 1,
        }}
      >
        {isExiting ? "..." : "Çıkış"}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
